import type { ReactNode } from "react";

import { cn } from "~/utils";

export type DiffLineStyle =
  | "context"
  | "added"
  | "removed"
  | "hunk"
  | "header"
  | "metadata";

export interface DiffLineItem {
  style: DiffLineStyle;
  content: ReactNode;
  prefix?: ReactNode;
}

export function unifiedDiffLines(patch: string): DiffLineItem[] {
  const lines = patch.endsWith("\n")
    ? patch.slice(0, -1).split("\n")
    : patch.split("\n");

  return lines.map((line) => ({
    style: unifiedDiffLineStyle(line),
    content: line,
  }));
}

function unifiedDiffLineStyle(line: string): DiffLineStyle {
  if (line.startsWith("--- ") || line.startsWith("+++ ")) return "header";
  if (line.startsWith("@@")) return "hunk";
  if (line.startsWith("+")) return "added";
  if (line.startsWith("-")) return "removed";
  if (line.startsWith("\\ ")) return "metadata";
  return "context";
}

export function DiffLines({
  lines,
  className,
}: {
  lines: DiffLineItem[];
  className?: string;
}) {
  return (
    <pre
      className={cn("overflow-x-auto font-mono text-xs leading-5", className)}
    >
      {lines.map((line, index) => (
        <span
          key={index}
          className={cn(
            "block min-w-max whitespace-pre px-2 text-slate-400",
            line.style === "added" && "bg-green-950/60 text-green-200",
            line.style === "removed" && "bg-red-950/60 text-red-200",
            line.style === "hunk" && "bg-blue-950/60 text-blue-200",
            line.style === "header" && "font-semibold text-slate-200",
            line.style === "metadata" && "text-slate-500",
          )}
        >
          {line.prefix !== undefined ? (
            <span className="mr-3 inline-block w-4 select-none text-slate-500">
              {line.prefix}
            </span>
          ) : null}
          {line.content === "" ? " " : line.content}
        </span>
      ))}
    </pre>
  );
}
